import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Users, School, BookOpen, Layers, FileText, UserPlus, Download, Loader2, PlayCircle } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { StatCard, PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const GENDER_COLORS = ['#3B6FB6', '#D9482B'];

export default function SchoolDashboardPage() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillMode, setBackfillMode] = useState('dry'); // 'dry' | 'live'
  const [executing, setExecuting] = useState(false);
  const [confirmLiveOpen, setConfirmLiveOpen] = useState(false);

  const handleBackfillDryRun = async () => {
    try {
      setBackfilling(true);
      const res = await schoolApi.backfillEnrollmentsDryRun();
      setBackfillResult(res);
      setBackfillMode('dry');
      setBackfillOpen(true);
      toast.success(t('dash.backfillDone', 'Dry run complete — no changes made'));
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 403
        ? t('dash.backfillForbidden', 'Only a super admin can run this backfill')
        : (e?.response?.data?.detail || t('dash.backfillFailed', 'Backfill dry run failed')));
    } finally {
      setBackfilling(false);
    }
  };

  const handleBackfillLive = async () => {
    try {
      setExecuting(true);
      const res = await schoolApi.backfillEnrollmentsLive();
      setBackfillResult(res);
      setBackfillMode('live');
      setConfirmLiveOpen(false);
      setBackfillOpen(true);
      toast.success(t('dash.backfillLiveDone', 'Backfill executed'));
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 403
        ? t('dash.backfillForbidden', 'Only a super admin can run this backfill')
        : (e?.response?.data?.detail || t('dash.backfillLiveFailed', 'Backfill execution failed')));
    } finally {
      setExecuting(false);
    }
  };

  const handleExportEnrollments = async () => {
    try {
      setExporting(true);
      const res = await schoolApi.exportEnrollments();
      // Derive filename from the Content-Disposition header (fallback to a default).
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : `school_enrollments_${orgId}.json`;
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(t('dash.enrollmentsExported', 'Enrollments exported'));
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 403
        ? t('dash.exportForbidden', 'Only a super admin can export enrollments')
        : t('dash.exportFailed', 'Failed to export enrollments'));
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const d = await schoolApi.dashboard(orgId, selectedYearId);
      setData(d);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [orgId, selectedYearId]);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    try {
      setSeeding(true);
      await schoolApi.seedDemo();
      toast.success('Demo data loaded');
      window.location.reload();
    } catch (e) {
      toast.error('Failed to load demo data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <div className="max-w-[1400px] mx-auto"><LoadingState rows={6} /></div>;
  }

  const counts = data?.counts || {};
  const isEmpty = !data || (counts.students === 0 && counts.classes === 0);

  if (isEmpty) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <PageHeader title={t('nav.dashboard')} subtitle={t('app.school')} icon={GraduationCap} />
        <Card>
          <EmptyState
            icon={GraduationCap}
            title={t('dash.noStudents')}
            description="Load demo data to explore the school management system instantly."
            actionLabel={seeding ? t('common.loading') : 'Load Demo Data'}
            onAction={handleSeed}
            testId="dashboard-empty"
          />
        </Card>
      </div>
    );
  }

  const genderData = [
    { name: t('common.male'), value: data.gender?.male || 0 },
    { name: t('common.female'), value: data.gender?.female || 0 },
  ];
  const classDist = (data.class_distribution || []).map((c) => ({ name: c.class_name, students: c.student_count }));

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader
        title={`${t('dash.welcome')}, ${user?.name?.split(' ')[0] || ''}`}
        subtitle={t('dash.overview')}
        icon={GraduationCap}
        actions={
          <div className="flex items-center gap-2">
            {user?.role === 'super_admin' && (
              <Button
                variant="outline"
                onClick={handleBackfillDryRun}
                disabled={backfilling}
                className="gap-2"
                data-testid="dashboard-backfill-dryrun"
              >
                {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                {t('dash.backfillDryRun', 'Run Enrollment Backfill (Dry Run)')}
              </Button>
            )}
            {user?.role === 'super_admin' && (
              <Button
                variant="destructive"
                onClick={() => setConfirmLiveOpen(true)}
                disabled={executing}
                className="gap-2"
                data-testid="dashboard-backfill-live"
              >
                {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                {t('dash.backfillLive', 'Execute Enrollment Backfill (Live)')}
              </Button>
            )}
            {user?.role === 'super_admin' && (
              <Button
                variant="outline"
                onClick={handleExportEnrollments}
                disabled={exporting}
                className="gap-2"
                data-testid="dashboard-export-enrollments"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {t('dash.exportEnrollments', 'Export Enrollments (JSON)')}
              </Button>
            )}
            <Button onClick={() => navigate('/school/students')} className="gap-2" data-testid="dashboard-new-admission">
              <UserPlus className="w-4 h-4" /> {t('students.new')}
            </Button>
          </div>
        }
      />

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label={t('dash.enrolledThisYear', 'Enrolled this year')} value={counts.students} icon={GraduationCap} accent="students" testId="stat-students" />
        <StatCard label={t('dash.registeredTotal', 'Registered students (all years)')} value={counts.registered_total} icon={Users} accent="classes" testId="stat-registered-total" />
        <StatCard label={t('dash.teachers')} value={counts.teachers} icon={Users} accent="teachers" testId="stat-teachers" />
        <StatCard label={t('dash.classes')} value={counts.classes} icon={School} accent="classes" testId="stat-classes" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
        <StatCard label={t('dash.subjects')} value={counts.subjects} icon={BookOpen} accent="exams" testId="stat-subjects" />
        <StatCard label={t('dash.sections')} value={counts.sections} icon={Layers} accent="students" />
        <StatCard label={t('dash.exams')} value={counts.exams} icon={FileText} accent="exams" />
        <StatCard label={t('students.guardian')} value={counts.parents} icon={Users} accent="teachers" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('dash.genderSplit')}</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={genderData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {genderData.map((entry, i) => <Cell key={i} fill={GENDER_COLORS[i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2 text-sm">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: GENDER_COLORS[0] }} /> {t('common.male')}: {data.gender?.male || 0}</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: GENDER_COLORS[1] }} /> {t('common.female')}: {data.gender?.female || 0}</span>
          </div>
        </Card>

        <Card className="p-5 xl:col-span-2">
          <h3 className="text-sm font-semibold mb-3">{t('dash.classDistribution')}</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={classDist} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.6} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip cursor={{ fill: 'rgba(59,111,182,0.08)' }} />
                <Bar dataKey="students" fill="#3B6FB6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Recent admissions */}
      <Card className="mt-4">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold">{t('dash.recentAdmissions')}</h3>
        </div>
        <div className="divide-y divide-border">
          {(data.recent_admissions || []).map((s) => (
            <div key={s.id} className="px-5 py-3 flex items-center justify-between hover:bg-muted/40" data-testid={`recent-admission-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                  {s.name?.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.admission_no} &middot; {s.class_name || '-'}</p>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">{(s.admission_date || s.created_at || '').slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent className="max-w-lg" data-testid="backfill-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {backfillMode === 'live'
                ? t('dash.backfillLive', 'Execute Enrollment Backfill (Live)')
                : t('dash.backfillDryRun', 'Run Enrollment Backfill (Dry Run)')}
            </DialogTitle>
            <DialogDescription>
              {backfillMode === 'live'
                ? t('dash.backfillLiveDesc', 'Live run — records were created, de-duplicated, and indexed as reported below.')
                : t('dash.backfillDryRunDesc', 'Preview only — no records were created, deleted, or indexed.')}
            </DialogDescription>
          </DialogHeader>
          <pre
            className="max-h-[60vh] overflow-auto rounded-md bg-muted p-4 text-xs leading-relaxed"
            data-testid="backfill-result-json"
          >
            {backfillResult ? JSON.stringify(backfillResult, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmLiveOpen} onOpenChange={setConfirmLiveOpen}>
        <AlertDialogContent data-testid="backfill-live-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dash.backfillLiveConfirmTitle', 'Execute live enrollment backfill?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dash.backfillLiveConfirmDesc', 'This will create missing 2025-2026 enrollments, delete duplicate records, and add a unique index in the live database. This cannot be undone. Run the Dry Run first to review the impact.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing} data-testid="backfill-live-cancel">
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBackfillLive(); }}
              disabled={executing}
              data-testid="backfill-live-confirm-btn"
            >
              {executing ? t('dash.backfillRunning', 'Running…') : t('dash.backfillLiveConfirmYes', 'Yes, execute')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
